/**
 * Lottery operations panels for `/lotteries/:id` and the Review step: §5.4 publish gates (server), §16.1 step 8
 * share links + QR, §12.1 platforms editor, console overview metrics and pending-version actions (approve /
 * publish of a material mid-flight change, D-1103). Every mutation goes through the admin API; nothing is decided
 * client-side.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SocialPlatform,
  type ConfigVersionDto,
  type LotteryAdminDto,
  type ValidateLotteryResponse,
} from '@windrop/contracts';
import { Button, Field, Input, Select, Skeleton, useIntl, useToast } from '@windrop/ui';
import { useEffect, useState } from 'react';
import { Can } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { NoDataYet } from '../../components/EmptyState';
import { StatCard } from '../../components/StatCard';
import { ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { lotteriesOpsApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';

export function PublishGates({ lotteryId, version }: { lotteryId: string; version?: number }) {
  const query = useQuery({
    queryKey: ['lottery', lotteryId, 'gates', version ?? 'head'],
    queryFn: () => lotteriesOpsApi.validate(lotteryId, version),
    retry: false,
  });
  if (query.isPending) return <Skeleton shape="block" height={120} label="Running publish gates" />;
  if (query.isError)
    return (
      <p className="text-error">
        Publish gates could not be evaluated{isApiError(query.error) ? `: ${query.error.message}` : ''}.
      </p>
    );
  const g = query.data;
  return <GatesTable gates={g} />;
}

export function GatesTable({ gates: g }: { gates: ValidateLotteryResponse }) {
  const intl = useIntl();
  return (
    <div className="wd-stack" data-testid="publish-gates">
      <p>
        {g.ok ? (
          <ToneBadge tone="success">Publishable</ToneBadge>
        ) : (
          <ToneBadge tone="error">Blocked</ToneBadge>
        )}{' '}
        <span className="wd-muted">
          v{g.configVersion} · liability {intl.formatMoney(g.liability.amountMinor, g.liability.currency)} ·
          checked {intl.formatRelative(g.checkedAt)}
          {g.requiresDualControl ? ' · second-person approval required' : ''}
        </span>
      </p>
      <table className="wdc-table">
        <caption className="wd-sr-only">Publish gates</caption>
        <thead>
          <tr>
            <th scope="col">Gate</th>
            <th scope="col">Result</th>
            <th scope="col">Detail</th>
          </tr>
        </thead>
        <tbody>
          {g.gates.map((x) => (
            <tr key={x.gate}>
              <th scope="row">{humanizeStatus(x.gate)}</th>
              <td>
                {x.ok ? (
                  <ToneBadge tone="success">Pass</ToneBadge>
                ) : (
                  <ToneBadge tone="error">Blocked</ToneBadge>
                )}
              </td>
              <td>{x.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {g.issues.length ? (
        <details>
          <summary>
            {g.issues.length} validation issue{g.issues.length === 1 ? '' : 's'}
          </summary>
          <ul>
            {g.issues.map((i) => (
              <li key={`${i.path}-${i.code}`}>
                <ToneBadge tone={i.severity === 'error' ? 'error' : 'warning'}>{i.severity}</ToneBadge>{' '}
                {i.message} <code>{i.path}</code>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

export function ShareLinksPanel({ lotteryId }: { lotteryId: string }) {
  const { toast } = useToast();
  const query = useQuery({
    queryKey: ['lottery', lotteryId, 'share-links'],
    queryFn: () => lotteriesOpsApi.shareLinks(lotteryId),
    retry: false,
  });
  if (query.isPending) return <Skeleton shape="block" height={120} label="Loading share links" />;
  if (query.isError)
    return (
      <p className="text-error">
        Share links are not available{isApiError(query.error) ? `: ${query.error.message}` : ''}.
      </p>
    );
  const s = query.data;
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copied', description: text, tone: 'success' });
    } catch {
      toast({ title: 'Copy failed', description: 'Select the link and copy it manually.', tone: 'neutral' });
    }
  };
  return (
    <div
      className="wd-row"
      style={{ alignItems: 'flex-start', gap: 'var(--wd-space-4)', flexWrap: 'wrap' }}
      data-testid="share-links"
    >
      <img src={s.qrDataUrl} alt={`QR code for ${s.canonicalUrl}`} width={160} height={160} />
      <div className="wd-stack" style={{ flex: 1, minWidth: 260 }}>
        <p className="wd-muted">
          {s.publishedConfigVersion
            ? `Published v${s.publishedConfigVersion}.`
            : 'Not published yet — links resolve once the lottery is live.'}{' '}
          <a href={s.shareUrl} target="_blank" rel="noreferrer">
            Share page
          </a>{' '}
          ·{' '}
          <a href={s.cardUrl} target="_blank" rel="noreferrer">
            Share card
          </a>
        </p>
        <table className="wdc-table">
          <caption className="wd-sr-only">Share links per platform</caption>
          <thead>
            <tr>
              <th scope="col">Platform</th>
              <th scope="col">Link (UTM-tagged)</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {s.links.map((l) => (
              <tr key={l.platform}>
                <th scope="row">{l.label}</th>
                <td>
                  <code style={{ wordBreak: 'break-all' }}>{l.url}</code>
                  {l.accountUrl ? <div className="wd-muted">account: {l.accountUrl}</div> : null}
                </td>
                <td>
                  <Button variant="quiet" onClick={() => void copy(l.url)}>
                    Copy
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface PlatformRow {
  platform: SocialPlatform;
  accountUrl: string;
  accountLabel: string;
  isEnabled: boolean;
}

export function PlatformsEditor({
  lotteryId,
  status,
}: {
  lotteryId: string;
  status: LotteryAdminDto['status'];
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const query = useQuery({
    queryKey: ['lottery', lotteryId, 'platforms'],
    queryFn: () => lotteriesOpsApi.platforms(lotteryId),
    retry: false,
  });
  const [rows, setRows] = useState<PlatformRow[] | null>(null);
  useEffect(() => {
    if (query.data && rows === null)
      setRows(
        query.data.items.map((i) => ({
          platform: i.platform,
          accountUrl: i.accountUrl,
          accountLabel: i.accountLabel ?? '',
          isEnabled: i.isEnabled,
        })),
      );
  }, [query.data, rows]);
  const save = useMutation({
    mutationFn: (items: PlatformRow[]) =>
      lotteriesOpsApi.putPlatforms(lotteryId, {
        items: items.map((r, i) => ({
          platform: r.platform,
          accountUrl: r.accountUrl,
          accountLabel: r.accountLabel || null,
          displayOrder: i,
          isEnabled: r.isEnabled,
        })),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['lottery', lotteryId, 'platforms'], data);
      void queryClient.invalidateQueries({ queryKey: ['lottery', lotteryId, 'share-links'] });
      toast({
        title: 'Platforms saved',
        description: `${data.items.length} platform${data.items.length === 1 ? '' : 's'} in display order.`,
        tone: 'success',
      });
    },
  });
  const locked = ['CLOSED', 'DRAWN', 'SETTLED', 'CANCELLED'].includes(status);
  if (query.isPending || rows === null)
    return <Skeleton shape="block" height={120} label="Loading platforms" />;
  if (query.isError)
    return (
      <p className="text-error">
        Platforms are not available{isApiError(query.error) ? `: ${query.error.message}` : ''}.
      </p>
    );
  const update = (i: number, patch: Partial<PlatformRow>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    setRows(next);
  };
  return (
    <div className="wd-stack" data-testid="platforms-editor">
      <p className="wd-muted">
        The client&apos;s public account per platform, in display order (D06). Winners who arrived from a
        platform default to it; direct arrivals choose one at claim submission.
      </p>
      {rows.length === 0 ? <NoDataYet what="platforms" hint="Add the platforms the client is on." /> : null}
      {rows.map((r, i) => (
        <fieldset
          key={`${r.platform}-${i}`}
          className="wdc-form-grid wd-surface"
          style={{ padding: 'var(--wd-space-3)' }}
        >
          <Field label="Platform">
            <Select
              value={r.platform}
              onChange={(e) => update(i, { platform: e.target.value as SocialPlatform })}
              options={SocialPlatform.options.map((p) => ({ value: p, label: humanizeStatus(p) }))}
              disabled={locked}
            />
          </Field>
          <Field label="Account link" required>
            <Input
              type="url"
              value={r.accountUrl}
              onChange={(e) => update(i, { accountUrl: e.target.value })}
              disabled={locked}
            />
          </Field>
          <Field label="Label">
            <Input
              value={r.accountLabel}
              onChange={(e) => update(i, { accountLabel: e.target.value })}
              disabled={locked}
            />
          </Field>
          <label className="wd-row" style={{ gap: 'var(--wd-space-2)', alignSelf: 'end' }}>
            <input
              type="checkbox"
              checked={r.isEnabled}
              onChange={(e) => update(i, { isEnabled: e.target.checked })}
              disabled={locked}
            />{' '}
            Enabled
          </label>
          <div className="wd-btn-group">
            <Button variant="quiet" onClick={() => move(i, -1)} disabled={locked || i === 0}>
              Up
            </Button>
            <Button variant="quiet" onClick={() => move(i, 1)} disabled={locked || i === rows.length - 1}>
              Down
            </Button>
            <Button variant="quiet" onClick={() => setRows(rows.filter((_, j) => j !== i))} disabled={locked}>
              Remove
            </Button>
          </div>
        </fieldset>
      ))}
      <Can permission="lottery.update">
        <div className="wd-btn-group">
          <Button
            variant="secondary"
            onClick={() =>
              setRows([
                ...rows,
                { platform: 'WHATSAPP', accountUrl: 'https://wa.me/', accountLabel: '', isEnabled: true },
              ])
            }
            disabled={locked}
          >
            Add platform
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate(rows)}
            loading={save.isPending}
            disabled={locked}
            disabledReason={locked ? 'A closed or cancelled lottery cannot change.' : undefined}
            data-testid="save-platforms"
          >
            Save platforms
          </Button>
        </div>
      </Can>
      {save.isError ? (
        <p role="alert" className="text-error">
          {isApiError(save.error) ? save.error.message : 'Platforms were not saved.'}
        </p>
      ) : null}
    </div>
  );
}

export function MetricsPanel({ lotteryId }: { lotteryId: string }) {
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['lottery', lotteryId, 'metrics'],
    queryFn: () => lotteriesOpsApi.metrics(lotteryId),
    retry: false,
    refetchInterval: 30_000,
  });
  const m = query.data;
  const loading = query.isPending;
  return (
    <div className="wdc-grid" data-testid="lottery-metrics">
      <StatCard
        label="Confirmed entries"
        value={m ? intl.formatNumber(m.entries.eligible) : null}
        hint={
          m
            ? `${intl.formatNumber(m.entries.pending)} pending payment · ${intl.formatNumber(m.entries.refunded)} refunded`
            : undefined
        }
        loading={loading}
      />
      <StatCard
        label="Unique participants"
        value={m ? intl.formatNumber(m.entries.uniqueParticipants) : null}
        loading={loading}
      />
      <StatCard
        label="Wins"
        value={m?.wins ? intl.formatNumber(m.wins.total) : null}
        hint={m?.wins ? `${intl.formatNumber(m.wins.sponsorSamples)} sponsor samples` : undefined}
        loading={loading}
        emptyHint="No wins recorded yet."
      />
      <StatCard
        label="Claims"
        value={m?.claims ? `${intl.formatNumber(m.claims.open)} open` : null}
        hint={m?.claims ? `${intl.formatNumber(m.claims.approved)} approved` : undefined}
        loading={loading}
        emptyHint="No claims yet."
      />
      <StatCard
        label="Seconds to close"
        value={m?.schedule.secondsToClose != null ? intl.formatNumber(m.schedule.secondsToClose) : null}
        hint={m?.schedule.closesAt ? `closes ${intl.formatDateTime(m.schedule.closesAt)}` : undefined}
        loading={loading}
      />
      <StatCard
        label="Entries by platform"
        value={
          m
            ? m.byPlatform.length
              ? m.byPlatform
                  .map((p) => `${humanizeStatus(p.platform)} ${intl.formatNumber(p.count)}`)
                  .join(' · ')
              : 'No entries yet'
            : null
        }
        loading={loading}
      />
    </div>
  );
}

/** Pending (unpublished) versions of a published lottery with approve / publish actions (D-1103). */
export function PendingVersionActions({
  lottery,
  versions,
}: {
  lottery: LotteryAdminDto;
  versions: ConfigVersionDto[];
}) {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const pending = versions.filter(
    (v) =>
      !v.published &&
      lottery.publishedConfigVersion !== null &&
      v.configVersion > (lottery.publishedConfigVersion ?? 0),
  );
  const approve = useMutation({
    mutationFn: (v: number) => lotteriesOpsApi.approveVersion(lottery.lotteryId, v),
    onSuccess: (v) => {
      toast({ title: `v${v.configVersion} approved`, tone: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['lottery', lottery.lotteryId] });
    },
  });
  const publish = useMutation({
    mutationFn: (v: number) => lotteriesOpsApi.publishVersion(lottery.lotteryId, v),
    onSuccess: (v) => {
      toast({
        title: `v${v.configVersion} published`,
        description: 'A new seed commitment was recorded (§9.1).',
        tone: 'success',
      });
      void queryClient.invalidateQueries({ queryKey: ['lottery', lottery.lotteryId] });
      void queryClient.invalidateQueries({ queryKey: ['lotteries'] });
    },
  });
  if (pending.length === 0) return null;
  const error = approve.error ?? publish.error;
  return (
    <section className="wdc-callout" data-tone="warning" data-testid="pending-versions">
      {pending.map((v) => (
        <div key={v.configVersion} className="wd-stack">
          <p>
            <strong>Pending material change v{v.configVersion}</strong>{' '}
            {v.changeNote ? `— ${v.changeNote}` : ''}
            {v.participantRemedy ? (
              <>
                <br />
                Participant remedy: {v.participantRemedy}
              </>
            ) : null}
            <br />
            {v.approvedBy ? (
              <ToneBadge tone="info">Approved{v.approvedBy === session?.staffId ? ' by you' : ''}</ToneBadge>
            ) : (
              <ToneBadge tone="warning">Awaiting approval</ToneBadge>
            )}
          </p>
          <div className="wd-btn-group">
            {!v.approvedBy ? (
              <Can permission="lottery.approve">
                <Button
                  variant="secondary"
                  onClick={() => approve.mutate(v.configVersion)}
                  loading={approve.isPending}
                  disabled={v.createdBy?.staffId === session?.staffId}
                  disabledReason={
                    v.createdBy?.staffId === session?.staffId
                      ? 'The author of a change cannot approve it (§3).'
                      : undefined
                  }
                >
                  Approve v{v.configVersion}
                </Button>
              </Can>
            ) : null}
            <Can permission="lottery.publish">
              <Button
                variant="primary"
                onClick={() => publish.mutate(v.configVersion)}
                loading={publish.isPending}
              >
                Publish v{v.configVersion}
              </Button>
            </Can>
          </div>
        </div>
      ))}
      {error ? (
        <p role="alert" className="text-error">
          {isApiError(error) ? error.message : 'The action did not complete.'}
        </p>
      ) : null}
    </section>
  );
}
